import { lazy } from 'react'
import WidgetCategory from '../../WidgetCategory'
import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import { Override } from '../../../utils/typescript'
import { parseHtmlConfigFields, toConfigDescriptions } from './html-config'

/**
 * Config variables for the CodeWidget. Kept `as const` so the prop types can be
 * derived from it (see CodeWidgetConfigOptions).
 */
export const codeWidgetOptions = [
  {
    name: 'query',
    type: 'yasgui',
    label:
      'SPARQL SELECT query. Its JSON results arrive as this.props.data and are ' +
      'exposed to the page as window.wistor.data (and window.WIDGET_DATA).',
  },
  {
    name: 'html',
    type: 'textarea',
    label:
      'A complete standalone HTML page. It runs in a sandboxed iframe, reads the ' +
      'SPARQL results from window.wistor.data, and can write back with ' +
      'window.wistor.updateQuads({ inserts, deletes }).',
  },
  {
    name: 'config',
    type: 'json',
    label:
      'Optional configuration object, exposed to the page as window.wistor.config ' +
      '(and window.WIDGET_CONFIG), e.g. { "layout": "LR" }',
  },
  {
    name: 'targetGraph',
    type: 'text',
    label:
      'Default named graph IRI for updateQuads. Used for triples the page sends ' +
      'without an explicit graph (a per-update graph or a TriG named graph overrides it).',
  },
] as const

export type RawCodeWidgetConfigOptions = ConfigurablePropsFromDescription<typeof codeWidgetOptions>

/*
 * `html` is a 'textarea' config var, which the shared type map leaves as `never`.
 * Override it locally (same pattern as IFCViewerV4) instead of touching the shared helper.
 */
export type CodeWidgetConfigOptions = Override<RawCodeWidgetConfigOptions, {
  html?: string | null
}>

export const codeWidgetDescription = {
  name: 'Code Widget',
  component: lazy(() => import('./code-widget')),
  label: 'Code Widget',
  category: WidgetCategory.experimental,
  variables: codeWidgetOptions,
  /*
   * Self-describing config: the config dialog renders extra fields declared by
   * the current page HTML (see html-config). Stored under the "configField:" prefix.
   */
  deriveExtraVariables: (config: Record<string, unknown>) =>
    toConfigDescriptions(parseHtmlConfigFields(typeof config.html === 'string' ? config.html : null)),
}
